// Kampanya modülü ortak yardımcıları (liste + detay sekmeleri ortak kullanır).
import { authHeaders, getODataRows } from '../listPageStyles';

// Backend Campaign.Type whitelist'i (N_PLUS_M | DISCOUNT_PCT | FREE_GIFT | BUNDLE).
// Not: "X Al Y Bedava" (N_PLUS_M) kaldırıldı — "Bedelsiz / Hediye" (FREE_GIFT) ile
// işlevsel olarak aynı. Eski N_PLUS_M kayıtları backend'de hâlâ geçerli; rozet/özet
// türetimi geriye dönük destekler.
export const TYPE_OPTIONS = [
  { value: 'DISCOUNT_PCT', label: 'İndirim (% / ₺)' },
  { value: 'FREE_GIFT', label: 'Bedelsiz / Hediye' },
  { value: 'BUNDLE', label: 'Paket' },
];

export const CONDITION_UNITS = [
  { value: 'QUANTITY', label: 'Miktar Bazlı' },
  { value: 'AMOUNT', label: 'Tutar Bazlı (₺)' },
  // VARIETY: çeşit bazlı — kademeli indirim, ürün çeşidi sayısına göre (TiersCsv minCount).
  { value: 'VARIETY', label: 'Çeşit Bazlı' },
];

// Müşteri hedef türleri. CUSTOMER (eski SINGLE+MULTI birleşti) = "Müşteri Seçimi".
// SUBGROUP = Müşteri Alt Grup, DYNAMIC = Müşteri Özelleştirilmiş Grup.
export const TARGET_TYPES = [
  { value: 'ALL', label: 'Tüm Müşteriler', desc: 'Tüm müşteriler için geçerli', icon: '◎', csvField: null, endpoint: null },
  { value: 'CUSTOMER', label: 'Müşteri Seçimi', desc: 'Seçili müşteriler', icon: '☺', csvField: 'targetCustomerIdsCsv', endpoint: '/api/customers' },
  { value: 'GROUP', label: 'Müşteri Grubu', desc: 'Grup bazlı hedefleme', icon: '⚇', csvField: 'targetCustomerGroupIdsCsv', endpoint: '/api/customergroups' },
  { value: 'SUBGROUP', label: 'Müşteri Alt Grup', desc: 'Alt grup bazlı hedefleme', icon: '⚇', csvField: 'targetCustomerSubGroupIdsCsv', endpoint: '/api/customersubgroups' },
  { value: 'DYNAMIC', label: 'Özelleştirilmiş Grup', desc: 'Müşteri özelleştirilmiş grup tanımları', icon: '✦', csvField: 'targetCustomerDynamicGroupIdsCsv', endpoint: '/api/customerdynamicgroups' },
];

// Eski kayıtlar (SINGLE/MULTI) yeni CUSTOMER türüne eşlenir.
export function normalizeTargetType(value) {
  const up = String(value || 'ALL').toUpperCase();
  if (up === 'SINGLE' || up === 'MULTI') return 'CUSTOMER';
  return TARGET_TYPES.some((t) => t.value === up) ? up : 'ALL';
}

// ── Biçimlendiriciler ──
export function fmtMoney(v) {
  const n = Number(v || 0);
  if (Number.isNaN(n)) return '0';
  return n.toLocaleString('tr-TR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
}

export function fmtDate(value) {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function toLocalDateInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function fromLocalDateInput(value) {
  if (!value) return null;
  try { return new Date(`${value}T00:00:00`).toISOString(); } catch { return null; }
}

export function daysLeft(endDate) {
  if (!endDate) return null;
  const d = new Date(endDate);
  if (Number.isNaN(d.getTime())) return null;
  const diff = Math.ceil((d.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
  return diff;
}

export function csvToArr(csv) {
  if (!csv) return [];
  return String(csv).split(',').map((x) => x.trim()).filter(Boolean);
}

export function arrToCsv(arr) {
  if (!arr || !arr.length) return '';
  return arr.filter(Boolean).join(',');
}

// ── Tür rozeti türetimi (ConditionUnit + ödül türünden) ──
export function deriveCampaignKind(row) {
  const cu = row.conditionUnit || row.ConditionUnit;
  const unit = cu === 'AMOUNT' ? 'Tutar Bazlı' : cu === 'VARIETY' ? 'Çeşit Bazlı' : 'Miktar Bazlı';
  const type = row.type || row.Type;
  const discAmount = Number(row.discountAmount ?? row.DiscountAmount ?? 0);
  switch (type) {
    case 'N_PLUS_M':
      return { label: `${unit} - Al Bedava`, tone: 'navy' };
    case 'FREE_GIFT':
      return { label: `${unit} - Hediye`, tone: 'green' };
    case 'DISCOUNT_PCT':
      return discAmount > 0
        ? { label: `${unit} - Sabit İndirim`, tone: 'amber' }
        : { label: `${unit} - % İndirim`, tone: 'blue' };
    case 'BUNDLE':
      return { label: 'Paket', tone: 'purple' };
    default:
      return { label: type || '-', tone: 'neutral' };
  }
}

// ── Kampanya koşulunu insan diline çevir ──
export function buildHumanRule(row) {
  const type = row.type || row.Type;
  const tQty = row.thresholdQty ?? row.ThresholdQty ?? 0;
  const fQty = row.freeQty ?? row.FreeQty ?? 0;
  const tAmt = row.thresholdAmount ?? row.ThresholdAmount ?? 0;
  const dPct = row.discountPct ?? row.DiscountPct ?? 0;
  const dAmt = row.discountAmount ?? row.DiscountAmount ?? 0;
  const unit = (row.conditionUnit || row.ConditionUnit) === 'AMOUNT';
  switch (type) {
    case 'N_PLUS_M':
      return `${tQty} Al ${fQty} Bedava`;
    case 'FREE_GIFT':
      return `${tQty} adet alana ${fQty} hediye`;
    case 'DISCOUNT_PCT': {
      const cond = unit ? `${fmtMoney(tAmt)}₺ üzeri` : `${tQty} adet`;
      const reward = Number(dAmt) > 0 ? `${fmtMoney(dAmt)}₺ indirim` : `%${dPct} indirim`;
      return `${cond} → ${reward}`;
    }
    case 'BUNDLE':
      return 'Paket kampanyası';
    default:
      return '-';
  }
}

// ── Kısıtlama özeti (liste "Kısıtlamalar" sütunu) ──
export function hasRestrictions(row) {
  return Number(row.maxApplications ?? row.MaxApplications ?? 0) > 0
    || Number(row.maxPerCustomer ?? row.MaxPerCustomer ?? 0) > 0
    || Number(row.maxPerDistributor ?? row.MaxPerDistributor ?? 0) > 0
    || Number(row.maxPerDay ?? row.MaxPerDay ?? 0) > 0
    || Number(row.budgetLimit ?? row.BudgetLimit ?? 0) > 0;
}

export function restrictionSummary(row) {
  const parts = [];
  const maxApp = Number(row.maxApplications ?? row.MaxApplications ?? 0);
  const maxCust = Number(row.maxPerCustomer ?? row.MaxPerCustomer ?? 0);
  const budget = Number(row.budgetLimit ?? row.BudgetLimit ?? 0);
  if (maxApp > 0) parts.push(`Maks: ${maxApp}`);
  if (maxCust > 0) parts.push(`Müşteri: ${maxCust}`);
  if (budget > 0) parts.push(`Bütçe: ${fmtMoney(budget)} ₺`);
  return parts.join(', ');
}

// ── Lookup etiketi ──
export function lookupLabel(item) {
  if (!item) return '';
  const code = item.textId || item.TextId || item.code || item.Code || '';
  const desc = item.description || item.Description || item.name || item.Name || item.title || item.Title || '';
  if (code && desc) return `${code} - ${desc}`;
  return desc || code || '';
}

// ── SearchableSelect fetchOptions fabrikaları (sayfalı: $top=20 + $skip, sonsuz kaydırma) ──
const PAGE_SIZE = 20;

export async function searchProducts(query, skip = 0) {
  const term = String(query || '').replace(/'/g, "''").toLowerCase();
  const filter = `contains(tolower(TextId),'${term}') or contains(tolower(Description),'${term}')`;
  // light=1 → ProductImage'ı dışla; aksi halde her satır ~1.7MB base64 görsel taşır.
  const url = `/api/products?$top=${PAGE_SIZE}&$skip=${skip}&$orderby=TextId&light=1&$filter=${encodeURIComponent(filter)}`;
  const r = await fetch(url, { headers: authHeaders() });
  if (!r.ok) return [];
  const data = await r.json();
  return getODataRows(data).map((p) => ({ value: p.id || p.Id, label: lookupLabel(p) }));
}

export async function searchCustomers(query, skip = 0) {
  const term = String(query || '').replace(/'/g, "''").toLowerCase();
  const filter = `contains(tolower(TextId),'${term}') or contains(tolower(Title),'${term}')`;
  const url = `/api/customers?$top=${PAGE_SIZE}&$skip=${skip}&$orderby=TextId&$filter=${encodeURIComponent(filter)}`;
  const r = await fetch(url, { headers: authHeaders() });
  if (!r.ok) return [];
  const data = await r.json();
  return getODataRows(data).map((c) => ({ value: c.id || c.Id, label: lookupLabel(c) }));
}

// Küçük referans listeleri: /Select ile tamamı bir kez çekilip cache'lenir, istemcide
// filtrelenir; sonsuz kaydırma için skip ile sayfa sayfa (PAGE_SIZE) dilimlenir.
function makeSelectSearcher(endpoint) {
  let cache = null;
  return async function search(query, skip = 0) {
    if (!cache) {
      const r = await fetch(endpoint, { headers: authHeaders() });
      cache = r.ok ? (await r.json()) : [];
      if (!Array.isArray(cache)) cache = getODataRows(cache);
    }
    const term = String(query || '').toLocaleLowerCase('tr-TR');
    return cache
      .map((g) => ({ value: g.id || g.Id, label: lookupLabel(g) }))
      .filter((o) => !term || o.label.toLocaleLowerCase('tr-TR').includes(term))
      .slice(skip, skip + PAGE_SIZE);
  };
}

export const searchCustomerGroups = makeSelectSearcher('/api/customergroups/Select');
export const searchCustomerSubGroups = makeSelectSearcher('/api/customersubgroups/Select');
export const searchCustomerDynamicGroups = makeSelectSearcher('/api/customerdynamicgroups/Select');
export const searchVendors = makeSelectSearcher('/api/vendors/Select');
export const searchProductGroups = makeSelectSearcher('/api/productgroups/Select');
export const searchProductSubGroups = makeSelectSearcher('/api/productsubgroups/Select');
export const searchProductDynamicGroups = makeSelectSearcher('/api/productdynamicgroups/Select');

// Miktar (QUANTITY) koşulu için ölçü birimleri.
export const QUANTITY_UNITS = [
  { value: 'Adet', label: 'Adet' },
  { value: 'Koli', label: 'Koli' },
  { value: 'Kutu', label: 'Kutu' },
  { value: 'Paket', label: 'Paket' },
  { value: 'Düzine', label: 'Düzine' },
  { value: 'Kg', label: 'Kg' },
  { value: 'Lt', label: 'Litre' },
];

// Ürün hedef türü — kapsamın hangi tanım listesinden seçileceğini belirler.
export const PRODUCT_TARGET_TYPES = [
  { value: 'PRODUCT', label: 'Ürün', desc: 'Doğrudan ürün kapsamı', icon: '☷', csvField: 'applicableProductIdsCsv', endpoint: '/api/products' },
  { value: 'GROUP', label: 'Ürün Grubu', desc: 'Grup (marka) bazlı kapsam', icon: '⚇', csvField: 'applicableGroupIdsCsv', endpoint: '/api/productgroups' },
  { value: 'SUBGROUP', label: 'Ürün Alt Grubu', desc: 'Alt grup bazlı kapsam', icon: '⚇', csvField: 'applicableSubGroupIdsCsv', endpoint: '/api/productsubgroups' },
  { value: 'DYNAMIC', label: 'Özelleştirilmiş Grup', desc: 'Ürün özelleştirilmiş grup tanımları', icon: '✦', csvField: 'applicableDynamicGroupIdsCsv', endpoint: '/api/productdynamicgroups' },
];

// Id listesinden etiket çöz (chip gösterimi için).
export async function resolveLabels(endpoint, ids) {
  const list = csvToArr(Array.isArray(ids) ? ids.join(',') : ids);
  if (!list.length) return {};
  const filter = list.map((id) => `Id eq ${id}`).join(' or ');
  const url = `${endpoint}?$top=200&$filter=${encodeURIComponent(filter)}`;
  const r = await fetch(url, { headers: authHeaders() });
  if (!r.ok) return {};
  const data = await r.json();
  const map = {};
  getODataRows(data).forEach((it) => { map[it.id || it.Id] = lookupLabel(it); });
  return map;
}
